import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { type ReactNode, useEffect, useState } from "react";

import { ApiConnector } from "../../../api/api-connector";
import { useSupportedDiagnoses } from "../../../catalogs/supported-diagnoses";
import { HelperTooltip } from "../../../components/ui-kit/base/helper-tooltip";
import { CellEnum } from "../../../components/ui-kit/tables/cells/cell-enum";
import { CellLastEdit } from "../../../components/ui-kit/tables/cells/cell-last-edit";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import { diagConfidence } from "../../../constants/constants";
import { SelectionMode } from "../../../models/data-model";
import { getEnumValuesByName } from "../../../utils/data-utils";
import { getObjectFromArray, requireNonNull } from "../../../utils/utils";
import { Patient } from "../patient";
import { Diagnoses, DiagnosesItem } from "./diagnoses";

export const DiagnosesTable = observer(function DiagnosesTable(props: {
  data: DiagnosesItem[];
  api: ApiConnector;
  patient: Patient;
  onInvalidated?: () => void;
  disableEdit?: boolean;
  disableHistory?: boolean;
  disablePagination?: boolean;
  persistState?: boolean;
  canSelect?: SelectionMode;
  onSelect?: (selected: string[]) => void;
  preselected?: string[];
  filterActions?: ReactNode;
}) {
  const { t } = useLingui();
  const supportedDiags = useSupportedDiagnoses();
  const [model] = useState(() => {
    const m = new Diagnoses(
      [],
      props.api.permissions,
      props.api.requireUserUUID(),
    );
    if (props.disableHistory) {
      m.hasHistory = false;
    }
    return m;
  });

  useEffect(() => {
    model.updateData(props.data);
  }, [props.data, model]);

  return (
    <DataTable
      filterActions={props.filterActions}
      persistState={props.persistState}
      editMode="dialog"
      model={model}
      itemKeyField="uuid"
      columns={[
        {
          header: (
            <span className="flex items-center gap-1">
              <Trans>Diagnosed</Trans>
              <HelperTooltip content={t`Dates are in UTC.`} />
            </span>
          ),
          label: t`Diagnosed`,
          attr: "diagnosed_at",
          type: "date",
          editable: true,
          sort: true,
          initSort: "desc",
        },
        {
          header: <Trans>Name</Trans>,
          attr: "diagnosis_name",
          type: "string",
          sort: true,
        },
        {
          header: <Trans>Value</Trans>,
          attr: "diagnosis_value",
          editable: true,
          editInDialog: true,
          cell: (p) => (
            <CellEnum
              {...p}
              attr={requireNonNull(p.attr)}
              values={getObjectFromArray(
                getEnumValuesByName(supportedDiags, p.item.diagnosis_name),
              )}
            />
          ),
        },
        {
          header: <Trans>Confidence</Trans>,
          attr: "diagnosis_confidence",
          type: "enum",
          editable: true,
          values: getObjectFromArray(diagConfidence.map(String)),
        },
        {
          header: (
            <span className="flex items-center gap-1">
              <Trans>Remission</Trans>
              <HelperTooltip content={t`Dates are in UTC.`} />
            </span>
          ),
          label: t`Remission`,
          attr: "remission",
          type: "date",
          editable: true,
        },
        {
          header: <Trans>Comment</Trans>,
          attr: "comment",
          type: "string",
          multiline: true,
          editable: true,
        },
        model.showingHistory && {
          header: <Trans>Added</Trans>,
          attr: "created_at",
          type: "datetime",
        },
        model.showingHistory && {
          header: <Trans>Last edit</Trans>,
          cell: ({ item }) => <CellLastEdit item={item} />,
        },
      ]}
      onEdit={
        props.disableEdit
          ? undefined
          : async (editedField, item) => {
              await props.patient.updateItem(
                "diagnoses",
                item.uuid,
                editedField,
              );
              props.onInvalidated?.();
            }
      }
      onDelete={
        props.disableEdit
          ? undefined
          : async (item) => {
              await props.patient.deleteItem("diagnoses", item.uuid);
              props.onInvalidated?.();
            }
      }
      rowsPerPage={props.disablePagination ? Number.MAX_VALUE : 25}
      canSelect={props.canSelect}
      onSelect={props.onSelect}
      preselected={props.preselected}
    />
  );
});
